/**
 * Overview Deck — glanceable, multi-angle visual summary of the evaluated
 * configuration (ADR-0007 / 2026-05-27 viz request).
 *
 * Goal: a buyer/manager understands the shape of the regulatory surface at a
 * glance, without reading. Expresses BOTH 4+2s — the lifecycle 4+2 (ABCDPU
 * lanes) and the regulatory dimensions (object / exposure / market) — through
 * several chart types.
 *
 * HARD honesty rules (same as the rest of the product):
 *   - NO aggregate compliance / readiness score. The five readiness tracks
 *     live in the independent ribbon and are NEVER plotted as a single
 *     radar/gauge area (that would read as a composite score — the red line
 *     enforced by ci:five-way-readiness). The radar here plots the
 *     DISTRIBUTION of applicable obligations across lifecycle lanes — a
 *     "surface shape", explicitly not a score.
 *   - Every number is a count or a categorical share. Advisory only.
 *
 * All charts are hand-rolled SVG/CSS — no charting dependency (ADR-0001 $5
 * envelope).
 */

import type { ComplianceRequirementCard, LifecycleLayer } from "@regpulse/contracts";
import type { EvaluateResponse } from "../services/api";
import { emptyLens, type LensState } from "./RegulatoryLens";

interface Props {
  evaluation: EvaluateResponse;
  cards: ComplianceRequirementCard[];
  /** Current Regulatory Lens — drives the Lens Impact matrix (reactive). */
  lens?: LensState;
}

const LANES: LifecycleLayer[] = ["A", "B", "C", "D", "P", "U"];
const KINDS: Array<{ key: ComplianceRequirementCard["obligationKind"]; label: string; color: string }> = [
  { key: "hard_law", label: "Hard law", color: "var(--ft-hard-law)" },
  { key: "evidence_standard", label: "Evidence", color: "var(--ft-evidence)" },
  { key: "market_force", label: "Market force", color: "var(--ft-market-force)" },
  { key: "ecosystem_signal", label: "Ecosystem", color: "var(--ft-ecosystem)" },
];

function countBy<T extends string>(
  cards: ComplianceRequirementCard[],
  pick: (c: ComplianceRequirementCard) => T,
): Map<T, number> {
  const m = new Map<T, number>();
  for (const c of cards) {
    if (c.applicabilityCategory !== "applicable") continue;
    const k = pick(c);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

export function OverviewDeck({ evaluation, cards, lens }: Props) {
  const activeLens = lens ?? emptyLens();
  const applicable = cards.filter((c) => c.applicabilityCategory === "applicable");
  const laneCounts = countBy(applicable, (c) => c.layer);
  const kindCounts = countBy(applicable, (c) => c.obligationKind);
  const maturityCounts = countBy(applicable, (c) => c.verificationMaturityTier);
  const domainCounts = countBy(applicable, (c) => c.regulatoryDomain);
  const exposureCounts = countBy(applicable, (c) => c.legalEffect);
  const marketCounts = countBy(applicable, (c) => c.jurisdictionCode);

  const counters = evaluation.readiness.counters;
  const visible = applicable.filter((c) => c.lifecycleState !== "PLACEHOLDER").length;
  const placeholder = Math.max(0, counters.applicable - visible);

  const laneSeries = LANES.map((l) => ({ label: l, value: laneCounts.get(l) ?? 0 }));

  return (
    <section
      data-testid="overview-deck"
      aria-label="Overview deck"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header style={{ marginBottom: "var(--space-12)" }}>
        <h3 className="type-h3" style={{ margin: 0, fontSize: 13 }}>
          Overview ·{" "}
          <span style={{ color: "var(--color-text-secondary)", fontWeight: 400 }}>
            the shape of this configuration at a glance
          </span>
        </h3>
        <p className="type-caption" style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}>
          Advisory distributions and counts of applicable obligations — never an aggregate
          compliance or readiness score. The five readiness tracks stay independent in the ribbon
          above.
        </p>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "var(--space-12)",
        }}
      >
        <ChartCard
          testId="deck-radar"
          title="Lifecycle surface (ABCDPU)"
          caption="Applicable obligation count per lifecycle lane — a distribution shape, NOT a readiness or compliance score."
        >
          <Radar series={laneSeries} />
        </ChartCard>

        <ChartCard
          testId="deck-lane-load"
          title="Load by lane × obligation kind"
          caption="Right-edge number = that lane's total; legend numbers = total per kind, matching the Obligation-kind donut (same applicable obligations, sliced by lane here and by kind there). Non-legal kinds are never folded into legal load."
        >
          <LaneKindStacks applicable={applicable} />
        </ChartCard>

        <ChartCard
          testId="deck-funnel"
          title="Applicability funnel"
          caption="How this configuration narrows the registry. Hidden and needs-config are shown, never silently dropped."
        >
          <Funnel
            rows={[
              { label: "Registry rules", value: cards.length, color: "var(--color-border-strong)" },
              { label: "Applicable", value: counters.applicable, color: "var(--ft-hard-law)" },
              { label: "Visible (excl. placeholder)", value: visible, color: "var(--color-accent)" },
            ]}
            footnotes={[
              `placeholder / curation-gap: ${placeholder}`,
              `hidden by config: ${counters.hidden_by_config}`,
              `needs config: ${counters.unknown_needs_config}`,
            ]}
          />
        </ChartCard>

        <ChartCard
          testId="deck-maturity"
          title="Verification posture"
          caption="How much of the applicable set is verified vs draft / unverified. This is provenance maturity, not compliance."
        >
          <SegmentBar
            segments={[
              { label: "Verified", value: maturityCounts.get("ACTIVE_VERIFIED") ?? 0, color: "var(--status-canonical-ok)" },
              { label: "Verify-note", value: maturityCounts.get("ACTIVE_VERIFY_NOTE") ?? 0, color: "var(--ft-evidence)" },
              { label: "Draft", value: maturityCounts.get("DRAFT") ?? 0, color: "var(--status-candidate-pending)" },
              { label: "Seed-unverified", value: maturityCounts.get("SEED_UNVERIFIED") ?? 0, color: "var(--color-text-disabled)" },
            ]}
          />
        </ChartCard>

        <ChartCard
          testId="deck-kind"
          title="Obligation kind"
          caption="Legal (hard law) vs the separately-tracked evidence standards and non-legal market-force / ecosystem signals."
        >
          <Donut slices={KINDS.map((k) => ({ label: k.label, value: kindCounts.get(k.key) ?? 0, color: k.color }))} />
        </ChartCard>

        <ChartCard
          testId="deck-domain"
          title="Regulatory domain (object)"
          caption="Where the regulatory surface concentrates across the 16 closed-set domains."
        >
          <HBars rows={sortedRows(domainCounts)} />
        </ChartCard>

        <ChartCard
          testId="deck-exposure"
          title="Legal exposure (categorical)"
          caption="Advisory effect-type distribution — categorical only, no risk score, no penalty amounts."
        >
          <HBars rows={sortedRows(exposureCounts)} />
        </ChartCard>

        <ChartCard
          testId="deck-market"
          title="Market / jurisdiction"
          caption="Applicable obligations per jurisdiction for this configuration."
        >
          <HBars rows={sortedRows(marketCounts)} />
        </ChartCard>

        <ChartCard
          testId="deck-lens-matrix"
          title="Lens impact · dimension × lifecycle"
          caption="Under the current Regulatory Lens, how matching cards fall across lifecycle lanes. No lens active = baseline lane distribution (muted); pick a facet and that dimension's row narrows to the selected slice. A summary, not a full heatmap; no re-evaluation."
        >
          <LensImpactMatrix applicable={applicable} lens={activeLens} />
        </ChartCard>
      </div>
    </section>
  );
}

function sortedRows(m: Map<string, number>): Array<{ label: string; value: number }> {
  return Array.from(m.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

/* ───────────────────────── Lens Impact Mini Matrix ───────────────────── */

const MATRIX_LANES: LifecycleLayer[] = ["A", "B", "C", "D", "P", "U"];
const MATRIX_ROWS: Array<{
  key: string;
  label: string;
  facet: keyof LensState;
  pick: (c: ComplianceRequirementCard) => string;
}> = [
  { key: "domain", label: "Object", facet: "domain", pick: (c) => c.regulatoryDomain },
  { key: "timing", label: "Timing", facet: "timing", pick: (c) => c.obligationTiming },
  { key: "market", label: "Market", facet: "jurisdiction", pick: (c) => c.jurisdictionCode },
  { key: "evidence", label: "Evidence", facet: "evidence", pick: (c) => c.coverageState },
];

function LensImpactMatrix({
  applicable,
  lens,
}: {
  applicable: ComplianceRequirementCard[];
  lens: LensState;
}) {
  // Baseline = all visible (non-placeholder) applicable cards per lane —
  // matches the lifecycle board's lane counts.
  const visible = applicable.filter((c) => c.lifecycleState !== "PLACEHOLDER");
  const baseline = MATRIX_LANES.map((lane) => visible.filter((c) => c.layer === lane).length);
  const baseMax = Math.max(1, ...baseline);
  const anyActive = MATRIX_ROWS.some((r) => lens[r.facet].size > 0);

  const cell = (n: number, max: number, saturated: boolean, k: string, title: string) => (
    <td
      key={k}
      title={title}
      style={{
        padding: "2px 4px",
        textAlign: "center",
        fontFamily: "var(--font-mono)",
        border: "1px solid var(--color-border)",
        color: n === 0 ? "var(--color-text-disabled)" : "var(--color-text-primary)",
        background:
          n === 0
            ? "transparent"
            : `color-mix(in srgb, var(--color-accent) ${Math.round(
                (n / max) * (saturated ? 72 : 22),
              )}%, transparent)`,
      }}
    >
      {n}
    </td>
  );

  return (
    <div data-testid="lens-impact-matrix" style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 10 }}>
        <thead>
          <tr>
            <th aria-label="dimension" style={{ padding: "2px 4px" }} />
            {MATRIX_LANES.map((l) => (
              <th
                key={l}
                style={{
                  padding: "2px 4px",
                  textAlign: "center",
                  color: "var(--color-text-secondary)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Baseline — always one reference row (all visible cards per lane). */}
          <tr data-testid="matrix-row-baseline">
            <th
              scope="row"
              style={{
                textAlign: "left",
                padding: "2px 6px 2px 0",
                whiteSpace: "nowrap",
                color: "var(--color-text-secondary)",
                fontWeight: 600,
              }}
            >
              Baseline
            </th>
            {baseline.map((n, i) =>
              cell(n, baseMax, false, MATRIX_LANES[i]!, `Baseline · lane ${MATRIX_LANES[i]}: ${n}`),
            )}
          </tr>

          {/* One row per lens dimension: only the ACTIVE one shows the selected
              distribution; the rest are muted "pick a facet" placeholders — no
              repeated baseline numbers. */}
          {MATRIX_ROWS.map((row) => {
            const sel = lens[row.facet];
            const active = sel.size > 0;
            if (!active) {
              return (
                <tr key={row.key} data-testid={`matrix-row-${row.key}`} data-active="false">
                  <th
                    scope="row"
                    style={{
                      textAlign: "left",
                      padding: "2px 6px 2px 0",
                      whiteSpace: "nowrap",
                      color: "var(--color-text-disabled)",
                      fontWeight: 400,
                    }}
                  >
                    {row.label}
                  </th>
                  <td
                    colSpan={MATRIX_LANES.length}
                    style={{
                      padding: "2px 6px",
                      color: "var(--color-text-disabled)",
                      fontStyle: "italic",
                    }}
                  >
                    Pick a {row.label} facet to see impact
                  </td>
                </tr>
              );
            }
            const counts = MATRIX_LANES.map(
              (lane) => visible.filter((c) => c.layer === lane && sel.has(row.pick(c))).length,
            );
            const rowMax = Math.max(1, ...counts);
            return (
              <tr key={row.key} data-testid={`matrix-row-${row.key}`} data-active="true">
                <th
                  scope="row"
                  style={{
                    textAlign: "left",
                    padding: "2px 6px 2px 0",
                    whiteSpace: "nowrap",
                    color: "var(--color-text-primary)",
                    fontWeight: 700,
                  }}
                >
                  {row.label}
                </th>
                {counts.map((n, i) =>
                  cell(n, rowMax, true, MATRIX_LANES[i]!, `${row.label} · lane ${MATRIX_LANES[i]}: ${n}`),
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p
        className="type-caption"
        style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)", fontSize: 9 }}
      >
        {anyActive
          ? "Baseline = all visible cards per lane; highlighted dimension rows show the active lens selection's distribution. No re-evaluation, no lane move."
          : "Baseline = all visible cards per lane. Pick a Regulatory Lens facet to see where that dimension's selection concentrates."}
      </p>
    </div>
  );
}

/* ───────────────────────── ChartCard shell ───────────────────────── */

function ChartCard({
  testId,
  title,
  caption,
  children,
}: {
  testId: string;
  title: string;
  caption: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-testid={testId}
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-4)",
        padding: "var(--space-8)",
        background: "var(--color-surface)",
        minWidth: 0,
      }}
    >
      <h4
        style={{
          margin: "0 0 var(--space-4) 0",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--color-text-secondary)",
        }}
      >
        {title}
      </h4>
      <div>{children}</div>
      <p className="type-caption" style={{ margin: "var(--space-8) 0 0 0", color: "var(--color-text-secondary)", fontSize: 10 }}>
        {caption}
      </p>
    </section>
  );
}

/* ───────────────────────── Radar (SVG) ───────────────────────── */

function Radar({ series }: { series: Array<{ label: string; value: number }> }) {
  const n = series.length;
  const max = Math.max(1, ...series.map((s) => s.value));
  const cx = 130;
  const cy = 105;
  const R = 72;
  const angle = (i: number) => ((-90 + (i * 360) / n) * Math.PI) / 180;
  const pt = (i: number, frac: number): [number, number] => [
    cx + Math.cos(angle(i)) * R * frac,
    cy + Math.sin(angle(i)) * R * frac,
  ];
  const dataPoints = series.map((s, i) => pt(i, s.value / max));
  const dataPath = dataPoints.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const ring = (frac: number) =>
    series.map((_, i) => pt(i, frac).map((x) => x.toFixed(1)).join(",")).join(" ");

  return (
    <svg viewBox="0 0 260 210" role="img" aria-label="lifecycle surface radar" style={{ width: "100%", height: "auto" }}>
      {[0.5, 1].map((f) => (
        <polygon key={f} points={ring(f)} fill="none" stroke="var(--color-border)" strokeWidth={1} />
      ))}
      {series.map((s, i) => {
        const [x, y] = pt(i, 1);
        const [lx, ly] = pt(i, 1.18);
        return (
          <g key={s.label}>
            <line x1={cx} y1={cy} x2={x} y2={y} stroke="var(--color-border)" strokeWidth={1} />
            <text
              x={lx}
              y={ly}
              fontSize={10}
              fontWeight={700}
              fill="var(--color-text-secondary)"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {s.label} {s.value}
            </text>
          </g>
        );
      })}
      <polygon
        points={dataPath}
        fill="color-mix(in srgb, var(--color-accent) 22%, transparent)"
        stroke="var(--color-accent)"
        strokeWidth={2}
      />
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={2.5} fill="var(--color-accent)" />
      ))}
    </svg>
  );
}

/* ───────────────────────── Lane × kind stacks ───────────────────────── */

function LaneKindStacks({ applicable }: { applicable: ComplianceRequirementCard[] }) {
  const byLane = new Map<LifecycleLayer, Map<string, number>>();
  for (const l of LANES) byLane.set(l, new Map());
  for (const c of applicable) {
    const m = byLane.get(c.layer);
    if (m) m.set(c.obligationKind, (m.get(c.obligationKind) ?? 0) + 1);
  }
  const laneTotals = LANES.map((l) => Array.from(byLane.get(l)!.values()).reduce((s, v) => s + v, 0));
  const max = Math.max(1, ...laneTotals);
  // Per-kind grand totals so the legend reconciles 1:1 with the donut.
  const kindTotals = new Map<string, number>();
  for (const c of applicable) kindTotals.set(c.obligationKind, (kindTotals.get(c.obligationKind) ?? 0) + 1);

  return (
    <div style={{ display: "grid", gap: 4 }}>
      {LANES.map((l, idx) => {
        const m = byLane.get(l)!;
        const total = laneTotals[idx]!;
        return (
          <div key={l} data-testid={`lane-stack-${l}`} style={{ display: "grid", gridTemplateColumns: "16px 1fr 28px", alignItems: "center", gap: 6, fontSize: 11 }}>
            <span style={{ fontWeight: 700 }}>{l}</span>
            <div style={{ display: "flex", height: 12, borderRadius: "var(--radius-4)", overflow: "hidden", background: "var(--color-surface-sunken)", border: "1px solid var(--color-border)" }}>
              {KINDS.map((k) => {
                const v = m.get(k.key) ?? 0;
                if (v === 0) return null;
                return <div key={k.key} title={`${l} · ${k.label}: ${v}`} style={{ width: `${(v / max) * 100}%`, background: k.color }} />;
              })}
            </div>
            <span className="type-mono" style={{ textAlign: "right", color: "var(--color-text-secondary)" }}>{total}</span>
          </div>
        );
      })}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-8)", marginTop: 4 }}>
        {KINDS.map((k) => (
          <span key={k.key} data-testid={`lane-legend-${k.key}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9, color: "var(--color-text-secondary)" }}>
            <span style={{ width: 8, height: 8, background: k.color, borderRadius: 2 }} />
            {k.label} <strong style={{ color: "var(--color-text-primary)" }}>{kindTotals.get(k.key) ?? 0}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────────── Funnel ───────────────────────── */

function Funnel({
  rows,
  footnotes,
}: {
  rows: Array<{ label: string; value: number; color: string }>;
  footnotes: string[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {rows.map((r) => (
        <div key={r.label} data-testid={`funnel-${r.label.replace(/[^A-Za-z0-9]/g, "_")}`} style={{ fontSize: 11 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "var(--color-text-secondary)" }}>{r.label}</span>
            <span className="type-mono" style={{ fontWeight: 700 }}>{r.value}</span>
          </div>
          <div style={{ height: 10, background: "var(--color-surface-sunken)", borderRadius: "var(--radius-4)", border: "1px solid var(--color-border)", overflow: "hidden" }}>
            <div style={{ width: `${(r.value / max) * 100}%`, height: "100%", background: r.color }} />
          </div>
        </div>
      ))}
      <ul style={{ margin: "4px 0 0 0", padding: 0, listStyle: "none", display: "flex", flexWrap: "wrap", gap: "var(--space-8)", fontSize: 10, color: "var(--color-text-secondary)" }}>
        {footnotes.map((f) => (
          <li key={f} className="type-mono">{f}</li>
        ))}
      </ul>
    </div>
  );
}

/* ───────────────────────── SegmentBar (single stacked bar) ───────────── */

function SegmentBar({ segments }: { segments: Array<{ label: string; value: number; color: string }> }) {
  const total = Math.max(1, segments.reduce((s, x) => s + x.value, 0));
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ display: "flex", height: 14, borderRadius: "var(--radius-4)", overflow: "hidden", border: "1px solid var(--color-border)", background: "var(--color-surface-sunken)" }}>
        {segments.map((s) => (s.value === 0 ? null : (
          <div key={s.label} title={`${s.label}: ${s.value}`} style={{ width: `${(s.value / total) * 100}%`, background: s.color }} />
        )))}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 2 }}>
        {segments.map((s) => (
          <li key={s.label} data-testid={`seg-${s.label.replace(/[^A-Za-z0-9]/g, "_")}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--color-text-secondary)" }}>
              <span style={{ width: 8, height: 8, background: s.color, borderRadius: 2 }} />
              {s.label}
            </span>
            <span className="type-mono">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ───────────────────────── Donut (conic-gradient) ───────────────────── */

function Donut({ slices }: { slices: Array<{ label: string; value: number; color: string }> }) {
  const total = Math.max(1, slices.reduce((s, x) => s + x.value, 0));
  let consumed = 0;
  const stops = slices
    .filter((s) => s.value > 0)
    .map((s) => {
      const start = (consumed / total) * 100;
      consumed += s.value;
      const end = (consumed / total) * 100;
      return `${s.color} ${start}% ${end}%`;
    });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-12)" }}>
      <div
        role="img"
        aria-label="obligation kind donut"
        style={{
          width: 84,
          height: 84,
          flex: "0 0 auto",
          borderRadius: "50%",
          background: stops.length ? `conic-gradient(${stops.join(", ")})` : "var(--color-surface-sunken)",
          maskImage: "radial-gradient(circle, transparent 42%, black 43%)",
          WebkitMaskImage: "radial-gradient(circle, transparent 42%, black 43%)",
          border: "1px solid var(--color-border)",
        }}
      />
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 2, flex: 1, minWidth: 0 }}>
        {slices.map((s) => (
          <li key={s.label} data-testid={`kind-${s.label.replace(/[^A-Za-z0-9]/g, "_")}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 10, gap: 6 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--color-text-secondary)" }}>
              <span style={{ width: 8, height: 8, background: s.color, borderRadius: 2 }} />
              {s.label}
            </span>
            <span className="type-mono">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ───────────────────────── HBars (horizontal bars) ───────────────────── */

function HBars({ rows }: { rows: Array<{ label: string; value: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  const shown = rows.filter((r) => r.value > 0);
  if (shown.length === 0) {
    return (
      <p className="type-caption" style={{ margin: 0, color: "var(--color-text-disabled)", fontStyle: "italic" }}>
        No applicable obligations in this dimension.
      </p>
    );
  }
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 3, maxHeight: 180, overflowY: "auto" }}>
      {shown.map((r) => (
        <li key={r.label} data-testid={`bar-${r.label.replace(/[^A-Za-z0-9]/g, "_")}`} style={{ display: "grid", gridTemplateColumns: "1fr 24px", alignItems: "center", gap: 6, fontSize: 10 }}>
          <div style={{ display: "grid", gap: 1, minWidth: 0 }}>
            <span style={{ color: "var(--color-text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</span>
            <div style={{ height: 6, background: "var(--color-surface-sunken)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
              <div style={{ width: `${(r.value / max) * 100}%`, height: "100%", background: "var(--color-accent)", borderRadius: "var(--radius-full)" }} />
            </div>
          </div>
          <span className="type-mono" style={{ textAlign: "right", fontWeight: 700 }}>{r.value}</span>
        </li>
      ))}
    </ul>
  );
}
